import React, { useState, useEffect, useRef } from 'react';
import './court.css';

const LiveScorePage = () => {
  const [selectedCourt, setSelectedCourt] = useState('all');
  const [selectedDate, setSelectedDate] = useState('2024-09-03');
  const [liveMatches, setLiveMatches] = useState([]);
  const [completedMatches, setCompletedMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  
  const pollingIntervalRef = useRef(null);
  
  const API_BASE_URL = 'https://api.asterickx.com/api/MatchScoring';
  const API_ALL_LIVE = `${API_BASE_URL}/GetAllLiveScore`;

  const courts = [
    { id: 'all', name: 'All Courts' },
    { id: 'court1', name: 'Court 1' },
    { id: 'court2', name: 'Court 2' },
    { id: 'court3', name: 'Court 3' },
    { id: 'court4', name: 'Court 4' },
  ];

  // Fetch detailed match data by ID
  const fetchMatchDetails = async (matchId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${matchId}`);
      
      if (!response.ok) {
        console.warn(`Could not fetch details for match ${matchId}`);
        return null;
      }
      
      const data = await response.json();
      console.log('Match details:', data);
      return data;
    } catch (error) {
      console.error(`Error fetching match ${matchId}:`, error);
      return null;
    }
  };

  // Convert API JSON to component format for live matches
  const convertApiDataToLiveMatch = (apiData, liveData) => {
    const matchData = apiData || liveData;
    
    const sets = matchData.sets || [];
    
    // Determine current set number based on which sets have scores
    let currentSetNumber = 1;
    if (liveData?.currentSetNumber) {
      currentSetNumber = liveData.currentSetNumber;
    } else {
      // Count how many sets have been completed (both scores > 0 and at least one reached 21)
      for (let i = 0; i < sets.length; i++) {
        const set = sets[i];
        if (set.side1Score > 0 || set.side2Score > 0) {
          currentSetNumber = i + 1;
        }
      }
    }
    
    const isDoubles = matchData.categoryName?.toLowerCase().includes('doubles');
    
    const side1Name = matchData.side1Label || 'Player 1';
    const side2Name = matchData.side2Label || 'Player 2';
    
    // Create score arrays with 3 sets (for best of 3)
    const side1Scores = [0, 0, 0];
    const side2Scores = [0, 0, 0];
    
    sets.forEach((set, index) => {
      if (index < 3) {
        side1Scores[index] = set.side1Score || 0;
        side2Scores[index] = set.side2Score || 0;
      }
    });
    
    console.log('Match current set:', currentSetNumber, 'Scores:', side1Scores, side2Scores);
    
    return {
      id: matchData.matchId || liveData.matchId,
      matchId: matchData.matchId || liveData.matchId,
      court: liveData?.courtNumber ? `court${liveData.courtNumber}` : (matchData.courtDisplayName ? `court${matchData.courtDisplayName}` : 'court1'),
      tournamentName: matchData.tournamentName,
      categoryName: matchData.categoryName,
      roundName: matchData.roundName,
      matchNumber: matchData.matchNumber,
      status: matchData.status || liveData?.matchStatus,
      currentSet: currentSetNumber,
      player1: {
        name: side1Name,
        state: matchData.side1State,
        score: side1Scores,
        serving: matchData.currentServerSide === 1
      },
      player2: {
        name: side2Name,
        state: matchData.side2State,
        score: side2Scores,
        serving: matchData.currentServerSide === 2
      },
      winnerSide: matchData.winnerSide,
      isDoubles: isDoubles
    };
  };

  // Convert API JSON to component format for completed matches
  const convertApiDataToCompletedMatch = (apiData, liveData) => {
    const matchData = apiData || liveData;
    
    const sets = matchData.sets || [];
    const isDoubles = matchData.categoryName?.toLowerCase().includes('doubles');
    
    const side1Name = matchData.side1Label || 'Player 1';
    const side2Name = matchData.side2Label || 'Player 2';
    
    const side1Score = [0, 0, 0];
    const side2Score = [0, 0, 0];
    
    sets.forEach((set, index) => {
      if (index < 3) {
        side1Score[index] = set.side1Score || 0;
        side2Score[index] = set.side2Score || 0;
      }
    });
    
    // Determine winner based on winnerSide
    const side1Winner = matchData.winnerSide === 1;
    const side2Winner = matchData.winnerSide === 2;
    
    if (isDoubles) {
      return {
        id: matchData.matchId || liveData.matchId,
        matchId: matchData.matchId || liveData.matchId,
        court: liveData?.courtNumber ? `court${liveData.courtNumber}` : (matchData.courtDisplayName ? `court${matchData.courtDisplayName}` : 'court1'),
        isDoubles: true,
        team1: {
          players: side1Name.split(' / '),
          state: matchData.side1State,
          score: side1Score.filter(s => s > 0),
          winner: side1Winner
        },
        team2: {
          players: side2Name.split(' / '),
          state: matchData.side2State,
          score: side2Score.filter(s => s > 0),
          winner: side2Winner
        }
      };
    } else {
      return {
        id: matchData.matchId || liveData.matchId,
        matchId: matchData.matchId || liveData.matchId,
        court: liveData?.courtNumber ? `court${liveData.courtNumber}` : (matchData.courtDisplayName ? `court${matchData.courtDisplayName}` : 'court1'),
        isDoubles: false,
        player1: {
          name: side1Name,
          state: matchData.side1State,
          score: side1Score.filter(s => s > 0),
          winner: side1Winner
        },
        player2: {
          name: side2Name,
          state: matchData.side2State,
          score: side2Score.filter(s => s > 0),
          winner: side2Winner
        }
      };
    }
  };

  // Fetch all matches and separate them by status
  const fetchAllMatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const response = await fetch(API_ALL_LIVE);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const allMatchesData = await response.json();
      console.log('Fetched all matches:', allMatchesData);
      
      if (!Array.isArray(allMatchesData) || allMatchesData.length === 0) {
        setLiveMatches([]);
        setCompletedMatches([]);
        if (!silent) setLoading(false);
        return;
      }
      
      const inProgressMatches = allMatchesData.filter(match => match.matchStatus === "In Progress");
      const completedMatchesData = allMatchesData.filter(match => match.matchStatus === "Completed");
      
      const detailedLiveMatches = await Promise.all(
        inProgressMatches.map(async (liveMatch) => {
          const details = await fetchMatchDetails(liveMatch.matchId);
          return convertApiDataToLiveMatch(details, liveMatch);
        })
      );
      
      const detailedCompletedMatches = await Promise.all(
        completedMatchesData.map(async (completedMatch) => {
          const details = await fetchMatchDetails(completedMatch.matchId);
          return convertApiDataToCompletedMatch(details, completedMatch);
        })
      );
      
      setLiveMatches(detailedLiveMatches);
      setCompletedMatches(detailedCompletedMatches);
      setLastUpdate(new Date().toLocaleTimeString());
      if (!silent) setLoading(false);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setError(error.message);
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMatches();

    if (pollingEnabled) {
      pollingIntervalRef.current = setInterval(() => {
        fetchAllMatches(true);
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [pollingEnabled]);

  const togglePolling = () => {
    setPollingEnabled(!pollingEnabled);
  };

  const filterByCourtMatch = (match) => {
    if (selectedCourt === 'all') return true;
    return match.court === selectedCourt;
  };

  const formatPlayerDisplay = (player) => {
    if (player.players) {
      return player.players.join(' , ');
    }
    return player.name;
  };

  const getMatchTitle = (match) => {
    if (match.isDoubles) {
      return `${match.team1.players.join('/')} (${match.team1}) vs ${match.team2.players.join('/')} (${match.team2})`;
    }
    return `${match.player1.name} (${match.player1.state}) vs ${match.player2.name} (${match.player2.state})`;
  };

  return (
    <div className="live-score-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h4 className="header-title">
            Yonex - Sunrise 4th Mini National Badminton Championship - 2025
          </h4>
          <div className="header-right">
            {/* <div className="header-logos">
              <div className="logo logo-india">🏸</div>
            </div> */}
            <div className="connection-status">
              {/* <span className={`status-indicator ${pollingEnabled ? 'connected' : 'disconnected'}`}>
                {pollingEnabled ? '🟢' : '🔴'}
              </span> */}
              {/* <span>{pollingEnabled ? 'Auto-refresh ON' : 'Auto-refresh OFF'}</span> */}
              {/* {lastUpdate && <span style={{ fontSize: '0.8rem', marginLeft: '10px' }}>
                Updated: {lastUpdate}
              </span>} */}
            </div>
            <div className="date-selector">
              <label>Select Date:</label>
              <input
                type="date"
                className="date-input"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Live Matches Section */}
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="live-indicator"></span>
              Live Matches
            </h2>
            <div className="court-selector">
              <label>Select Court:</label>
              <select
                className="court-dropdown"
                value={selectedCourt}
                onChange={(e) => setSelectedCourt(e.target.value)}
              >
                {courts.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div style={{ 
              background: '#fee', 
              border: '1px solid #fcc', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              color: '#c33'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="card-container">
            {liveMatches.filter(filterByCourtMatch).length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🏸</div>
                <p>No live matches available</p>
                <p style={{ fontSize: '0.9rem', marginTop: '10px', color: '#718096' }}>
                  {loading ? 'Loading matches...' : 'Matches will appear here when they start'}
                </p>
              </div>
            ) : (
              <div className="match-grid">
                {liveMatches.filter(filterByCourtMatch).map((match) => (
                  <div key={match.id} className="live-match-card">
                    <div className="match-card-header">
                      <span className="court-badge">
                        COURT {match.court.replace('court', '')}
                      </span>
                      {/* <span className="live-badge">
                        <span className="live-badge-dot"></span>
                        LIVE - Set {match.currentSet}
                      </span> */}
                    </div>

                    {match.tournamentName && (
                      <div className="match-info">
                        <div className="tournament-name">{match.tournamentName}</div>
                        <div className="category-name">{match.categoryName}</div>
                        <div className="round-name">{match.roundName} - {match.matchNumber}</div>
                      </div>
                    )}

                    {/* Player 1 / Team 1 */}
                    <div className={`player-row ${match.player1.serving ? 'serving' : ''}`}>
                      <div className="player-info">
                        {/* <span className="player-indicator yellow"></span> */}
                        <span className="player-name">{match.player1.name}</span>
                        {/* <span className="player-state">({match.player1.state})</span> */}
                      </div>
                      <div className="score-display">
                        {match.player1.score.map((s, i) => {
                          const isCurrentSet = (i + 1) === match.currentSet;
                          
                          return (
                            <span
                              key={i}
                              className={`score-set`}
                              style={{
                                backgroundColor: isCurrentSet ? '#rgb(229, 231, 235)' : 'rgb(107, 114, 128);',
                                color: isCurrentSet ? 'rgb(107, 114, 128);' : '#6b7280'
                              }}
                            >
                              {s}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Player 2 / Team 2 */}
                    <div className={`player-row ${match.player2.serving ? 'serving' : ''}`}>
                      <div className="player-info">
                        {/* <span className="player-indicator blue"></span> */}
                        <span className="player-name">{match.player2.name}</span>
                        {/* <span className="player-state">({match.player2.state})</span> */}
                      </div>
                      <div className="score-display">
                        {match.player2.score.map((s, i) => {
                          const isCurrentSet = (i + 1) === match.currentSet;
                          
                          return (
                            <span
                              key={i}
                              className={`score-set`}
                              style={{
                               backgroundColor: isCurrentSet ? '#rgb(229, 231, 235)' : 'rgb(107, 114, 128);',
                                color: isCurrentSet ? 'rgb(107, 114, 128);' : '#6b7280'
                              }}
                            >
                              {s}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Completed Matches Section */}
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Completed Matches</h2>
          </div>

          <div className="card-container">
            {completedMatches.filter(filterByCourtMatch).length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <p>No completed matches yet</p>
              </div>
            ) : (
              <div className="match-grid">
                {completedMatches.filter(filterByCourtMatch).map((match) => (
                  <div key={match.id} className="completed-match-card">
                    <div className="completed-header">{getMatchTitle(match)}</div>

                    {/* Player 1 / Team 1 */}
                    <div className={`completed-player-row ${(match.isDoubles ? match.team1?.winner : match.player1.winner) ? 'winner' : ''}`}>
                      <div className="player-result">
                        <span className="winner-icon">
                          {(match.isDoubles ? match.team1?.winner : match.player1.winner) ? '🏆' : '🏸'}
                        </span>
                        <span className="player-name">
                          {match.isDoubles ? formatPlayerDisplay(match.team1) : match.player1.name}
                        </span>
                        {/* <span className="player-state">
                          ({match.isDoubles ? match.team1?.state : match.player1.state})
                        </span> */}
                        {/* <span className={`result-badge ${(match.isDoubles ? match.team1?.winner : match.player1.winner) ? 'win' : 'loss'}`}>
                          {(match.isDoubles ? match.team1?.winner : match.player1.winner) ? 'W' : 'L'}
                        </span> */}
                      </div>
                      <div className="completed-scores">
                        {(match.isDoubles ? match.team1?.score : match.player1.score).map((s, i) => (
                          <span key={i} className="completed-score">{s}</span>
                        ))}
                      </div>
                    </div>

                    {/* Player 2 / Team 2 */}
                    <div className={`completed-player-row ${(match.isDoubles ? match.team2?.winner : match.player2.winner) ? 'winner' : ''}`}>
                      <div className="player-result">
                        <span className="winner-icon">
                          {(match.isDoubles ? match.team2?.winner : match.player2.winner) ? '🏆' : '🏸'}
                        </span>
                        <span className="player-name">
                          {match.isDoubles ? formatPlayerDisplay(match.team2) : match.player2.name}
                        </span>
                        {/* <span className="player-state">
                          ({match.isDoubles ? match.team2?.state : match.player2.state})
                        </span> */}
                        {/* <span className={`result-badge ${(match.isDoubles ? match.team2?.winner : match.player2.winner) ? 'win' : 'loss'}`}>
                          {(match.isDoubles ? match.team2?.winner : match.player2.winner) ? 'W' : 'L'}
                        </span> */}
                      </div>
                      <div className="completed-scores">
                        {(match.isDoubles ? match.team2?.score : match.player2.score).map((s, i) => (
                          <span key={i} className="completed-score">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default LiveScorePage;